import API from '@services/API';

import {
    environmentActionTypes,
    errorActionTypes,
    messagesActionTypes,
    modalActionTypes,
    spinnerActionTypes,
    commonSagaActionTypes,
    zipCodeActionTypes,
} from '@constants/actionTypes/commonActionTypes';
import { configs } from '@constants/configs/configs';
import { commonModalTypes } from '@constants/common/modals/commonModalTypes';
import { entityModalTypes, transferStatusTypes } from '@constants/entity/modals/entityModalTypes';
import { Dispatch } from 'redux';

let interval;

export const startSpinner = (message: string) => ({
    type: spinnerActionTypes.START_SPINNER,
    message,
});

export const stopSpinner = () => ({
    type: spinnerActionTypes.STOP_SPINNER,
});

export const setError = (errorData: Object) => ({
    //test 1
    type: errorActionTypes.SET_ERROR,
    errorData,
});

export const setUnreadMessageCount = (numberUnread: number | null) => ({
    type: environmentActionTypes.UPDATE_ICON_UNREAD_COUNT,
    numberUnread,
});

export const updateEnvironmentProp = (path: string, value: string) => ({
    type: environmentActionTypes.UPDATE_PROP,
    path,
    value,
});

export const setEnvironments = (envData: Object) => ({
    type: environmentActionTypes.SET_ENVIRONMENTS,
    envData,
});

export const getEnvironment = () => ({
    type: commonSagaActionTypes.GET_ENVIRONMENT,
});

export const setExplainerData = ({ pageName, data }: { pageName: string; data: Object }) => ({
    type: environmentActionTypes.UPDATE_EXPLAINER_DATA,
    pageName,
    data,
});

export const setLoadedMessages = (loadedLanguages: string[]) => ({
    type: messagesActionTypes.SET_LOADED_MESSAGES,
    loadedLanguages: loadedLanguages,
});

export const setMessages = (messages: Object, lang: string) => ({
    type: messagesActionTypes.SET_MESSAGES,
    messages,
    lang,
});

export const setLanguage = (language: string) => ({
    type: environmentActionTypes.CHANGE_LANG,
    language,
});

export const changeLang = (url: string) => async (dispatch: Dispatch) => {
    const data = await API.getData(url, dispatch, actions);
    if (data.message === 'success') {
        dispatch(setLanguage(data.lang));
    }
    
    return data.message === 'success';
};
export const hideModal = (modalType: string) => ({
    type: modalActionTypes.HIDE_MODAL,
    modalType,
});

export const hideAllModals = () => ({
    type: modalActionTypes.HIDE_ALL_MODALS,
});

export const showModal = ({ modalData, modalType }: { modalData?: Object; modalType: string | undefined }) => ({
    type: modalActionTypes.SHOW_MODAL,
    modalData,
    modalType,
});

const actions = { startSpinner, stopSpinner, setError };

export const fetchUnreadMessages =
    (...args: [string, string]) =>
        async (dispatch: Dispatch) => {
            const [url, body] = args;
            const params = [url, body, global.__ENV__.DEVELOPMENT ? 'GET' : undefined, false];

            const data = await API.postData(params, dispatch, actions);

            if (data) {
                dispatch(setUnreadMessageCount(data));
            } else {
                dispatch(setUnreadMessageCount(null));
            }
        };
export const setCountyData = (data: Object) => ({
    type: zipCodeActionTypes.SET_COUNTY_DATA,
    data,
});
export const clearCountyData = () => ({
    type: zipCodeActionTypes.CLEAR_COUNTY_DATA,
});

export const fetchCounties =
    (...args: [string]) =>
        async (dispatch: Dispatch) => {
            const [url] = args;
            const data = await API.getData(url, dispatch, actions, false);
            if (data) {
                dispatch(setCountyData({ ...data }));
            } else {
                console.error('Counties cannot be fetched', url);
            }
        };

export const fetchPingStatus =
    (...args: [string, { redirect: string }]) =>
        async (dispatch: Dispatch) => {
            const [url, redirect] = args;
            const data = await API.getData(url, dispatch, actions, false);
            if (typeof data !== 'undefined' && data !== null && data === -1) {
                const redirectUrl = location.origin + redirect['redirect'];
                window.location.href = `${redirectUrl}`;
            }
        };

export const fetchExplainerData = (pageName: string) => async (dispatch: Dispatch) => {
    const data = await API.getData(
        configs.endpoints.EXTERNAL_CONTENT.processReplacements({ pageName }),
        dispatch,
        actions,
    );

    if (data.success) {
        dispatch(setExplainerData({ pageName, data: data.payload }));
    }
};

export const fetchMockModal =
    (...args) =>
        async (dispatch: Dispatch) => {
            const [mockModalType, hhmIndex, areHhms] = args;

            const data = await API.getData(configs.endpoints.MODAL_MOCKS, dispatch, actions);

            if (data) {
                if (mockModalType === 'default') {
                    return null;
                }

                const setMock = () =>
                    dispatch(
                        showModal({
                            modalData: data[mockModalType].modalData,
                            modalType: mockModalType,
                        }),
                    );

                if (data[mockModalType].shouldHhmsPresent) {
                    if (!areHhms) {
                        console.log('There no any hhms');
                        return null;
                    } else {
                        setMock();
                        return null;
                    }
                }

                if (data[mockModalType].shouldCurrentHhmPresent) {
                    if (hhmIndex === null) {
                        console.log('Chose page with any household member');
                        return null;
                    } else {
                        setMock();
                        return null;
                    }
                }

                setMock();
            }
        };

/**
 * Action for async services to display App in progress modal. Once pending verification is over - run a cb.
 * @returns {function(...[*]=)}
 * @param onPendingFinishedCb
 */
export const checkVerificationPending =
    (onPendingFinishedCb: () => void) => async (dispatch: Dispatch, getState: Function) => {
        let cnt = 0;
        let timeout = setTimeout(fetchDelayPopupData, 100);

        async function fetchDelayPopupData() {
            cnt++;
            if (cnt > 30) {
                clearTimeout(timeout);
                dispatch(hideModal(commonModalTypes.APP_IN_PROGRESS));
            }
            const data = await API.getData(
                configs.endpoints.VERIFICATION_PENDING,
                dispatch,
                actions,
                false,
                getState(),
            );
            if (data && data.result === 'success' && data.pending) {
                dispatch(
                    showModal({
                        modalType: commonModalTypes.APP_IN_PROGRESS,
                    }),
                );
                return (timeout = setTimeout(fetchDelayPopupData, 5000));
            } else if (data && data.result === 'error') {
                console.error(data.message);
            } else {
                clearTimeout(timeout);
                dispatch(hideModal(commonModalTypes.APP_IN_PROGRESS));
                onPendingFinishedCb();
            }
        }

        return timeout;
    };

export const getBOBTransferStatus = (url: string, intervalFrequency: number) => async dispatch => {
    const data = await API.customGetData(url, dispatch, actions, {
        'cache-control': 'no-cache',
        'If-Modified-Since': '0',
    });
    dispatch(hideAllModals());
    if (data.status === transferStatusTypes.NONE) {
        return;
    }
    if (data.status === transferStatusTypes.IN_PROGRESS || data.status === transferStatusTypes.PENDING) {
        interval = setInterval(() => {
            return dispatch(getBOBTransferStatusInLoop(url));
        }, intervalFrequency);
    } else {
        clearInterval(interval);
    }
    let mType = getModalType(data.status);
    data &&
        dispatch(
            showModal({
                modalType: mType,
                modalData: data,
            }),
        );
};

export const getBOBTransferStatusInLoop = (url: string) => async dispatch => {
    const data = await API.customGetData(url, dispatch, actions, {
        'cache-control': 'no-cache',
        'If-Modified-Since': '0',
    });
    dispatch(hideAllModals());
    if (data.status === transferStatusTypes.NONE) {
        clearInterval(interval);
        return;
    }

    if (!(data.status === transferStatusTypes.IN_PROGRESS || data.status === transferStatusTypes.PENDING)) {
        clearInterval(interval);
    }

    let mType = getModalType(data.status);
    dispatch(
        showModal({
            modalType: mType,
            modalData: data,
        }),
    );
};

export const acknowledgeTransferStatus = (url: string) => async (dispatch: Dispatch) => {
    await API.customGetData(url, dispatch, actions, { 'cache-control': 'no-cache', 'If-Modified-Since': '0' });
};

export const getModalType = (status: string) => {
    switch (status) {
        case transferStatusTypes.IN_PROGRESS:
        case transferStatusTypes.PENDING: {
            return entityModalTypes.TRANSFER_IN_PROGRESS_MODAL;
        }
        case transferStatusTypes.SUCCESS: {
            return entityModalTypes.TRANSFER_SUCCESS_MODAL;
        }
        case transferStatusTypes.PARTIAL_SUCCESS: {
            return entityModalTypes.TRANSFER_PARTIAL_SUCCESS_MODAL;
        }
        case transferStatusTypes.FAILED: {
            return entityModalTypes.TRANSFER_FAILURE_MODAL;
        }
        case transferStatusTypes.INCOMPLETE: {
            return entityModalTypes.TRANSFER_INCOMPLETE_MODAL;
        }
    }
};

export const SET_SELECTED_MEMBER_DATA_REDUCER = 'SET_SELECTED_MEMBER_DATA_REDUCER';
export const setSelectedMember = (data: Object) => ({
    type: SET_SELECTED_MEMBER_DATA_REDUCER,
    selectedMember: data,
});

export const incrementCommunicationPreferenceConfirmationCount = () => ({
    type: 'INCREMENT_COMMUNICATION_PREFERENCE_CONFIRMATION_COUNT',
});

export const decrementCommunicationPreferenceConfirmationCount = () => ({
    type: 'DECREMENT_COMMUNICATION_PREFERENCE_CONFIRMATION_COUNT',
});

export const updateCommunicationPreferenceConfirmationCount = (communicationPrefReviewCount:number) => ({
    type: 'UPDATE_COMMUNICATION_PREFERENCE_CONFIRMATION_COUNT',
    communicationPrefReviewCount,
});
